import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Clock,
  Eye,
  TrendingUp,
  FileText,
  BarChart3,
  RefreshCw,
  Layers,
  Zap,
  Database,
  Cpu,
  FlaskConical,
  Code,
  HelpCircle,
  Calendar,
  Download,
  ExternalLink
} from 'lucide-react';
import SEOHead from '../../components/SEO/SEOHead';
import PortfolioComparisonImage from '../../components/PortfolioComparisonImage';

const CrossLicensingSolution = () => {
  const features = [
    {
      icon: BarChart3,
      title: 'Portfolio Comparison',
      description: 'Just provide patent numbers from both portfolios - nothing else required. Get instant comparative analysis showing relative portfolio values, patent quality distribution, technology overlap, and strength metrics. This gives you quantitative leverage for negotiations: "Our portfolio is valued at $X with 15 Grade A patents vs. their $Y with 8 Grade A patents."'
    },
    {
      icon: RefreshCw,
      title: 'Real-Time Valuation Updates',
      description: 'Market conditions change daily. Your valuation updates automatically as new data becomes available, ensuring you always negotiate with current numbers.'
    },
    {
      icon: FileText,
      title: 'Negotiation-Ready Reports',
      description: 'Generate executive summaries and detailed technical reports that your legal team can use immediately. Export to PDF or share via secure link.'
    },
    {
      icon: Layers,
      title: 'Technology Overlap Mapping',
      description: 'Identify which of your patents directly compete with or complement the other party\'s portfolio. Focus negotiations on the highest-value overlaps.'
    },
    {
      icon: Zap,
      title: 'Instant Bulk Patent Analysis',
      description: 'What takes traditional consultants $50K and 3 months for a single patent, PricePatent delivers instantly via API. Get portfolio valuation, patent-level grades (AA to C), industry benchmarking, and monetization candidate identification based on data from USPTO, WIPO, EPO, and 377M+ analyzed patents.'
    },
    {
      icon: Database,
      title: 'Custom Data Solutions',
      description: 'Need specific data points not included in our standard reports? We can provide customized analysis datasets tailored to your negotiation strategy. Contact us to discuss your specific data requirements.'
    }
  ];

  const painPoints = [
    {
      icon: Clock,
      title: 'Slow Manual Analysis',
      description: 'Traditional valuation takes weeks of expert review. By the time you have numbers, market conditions have changed and negotiation windows have closed.'
    },
    {
      icon: Eye,
      title: 'Incomplete Competitor Intelligence',
      description: 'Without visibility into how your portfolio stacks up against competitors, you\'re negotiating blind. Leaving millions on the table or accepting unfavorable terms.'
    },
    {
      icon: TrendingUp,
      title: 'Lack of Quantitative Leverage',
      description: '"Our patents are valuable" isn\'t enough. You need concrete numbers, portfolio valuations, and industry benchmarks to support your negotiating position.'
    }
  ];

  const steps = [
    {
      number: 1,
      title: 'Submit Patent Numbers',
      description: 'Simply provide patent numbers - that\'s all we need. No documents, no confidential data, no detailed disclosures. Our AI retrieves all analysis data from official patent office databases.',
      time: 'Takes 2 minutes'
    },
    {
      number: 2,
      title: 'AI Analysis & Comparison',
      description: 'Our AI evaluates 30+ factors per patent:',
      time: 'Results in 2.1 seconds',
      details: [
        'Citation networks (forward/backward citations)',
        'Legal strength (claims, rejections, oppositions)',
        'Technology relevance and market trends',
        'Geographic coverage and remaining patent life',
        'Industry-specific valuation factors',
        'Inventor quality'
      ]
    },
    {
      number: 3,
      title: 'Export Negotiation Package',
      description: 'Download comprehensive reports showing:',
      time: 'Ready to share with legal team',
      details: [
        'Individual patent valuations with quality grades (AA to C)',
        'Portfolio total values',
        'Raw Data Exports - Export all valuation data in CSV, Excel, or JSON format. Use our data in your own analysis tools, negotiation models, or IP management systems.',
        'Valuation Factors Per Patent - Our AI analyzes technical specifications, market relevance, citation networks, legal strength, geographic coverage, technology adoption rates, and additional factors for every patent.',
        'Monetization candidates ranked by business feasibility',
        'Industry benchmarks showing how portfolios compare to sector averages'
      ]
    }
  ];

  const industries = [
    {
      icon: Cpu,
      title: 'Semiconductor & Electronics',
      description: 'High citation density and rapid innovation cycles. Our analysis weighs forward citations heavily in fast-moving technology sectors where patent influence is critical.'
    },
    {
      icon: Code,
      title: 'Software & IT',
      description: 'Shorter technology lifecycles and higher patent volume. Our sector-specific models account for faster obsolescence rates and citation patterns unique to software patents.'
    },
    {
      icon: FlaskConical,
      title: 'Biotechnology & Pharma',
      description: 'Longer patent lifecycles and different citation patterns. Our analysis reflects the extended value period typical in pharmaceutical and biotech patents compared to other sectors.'
    }
  ];

  const faqs = [
    {
      question: 'How accurate are your cross-licensing valuations?',
      answer: 'Our AI analyzes 30+ factors including citation networks, legal strength, technology relevance, and market data from 377M+ patents. Valuations are based on established income-method principles adapted for big data analysis. For high-stakes negotiations, we recommend using our data-driven reports alongside traditional expert review for strategy.'
    },
    {
      question: 'Can I compare my portfolio against multiple competitors at once?',
      answer: 'Yes. Enterprise plans support multi-party portfolio comparisons, ideal for complex licensing negotiations or patent pool analysis.'
    },
    {
      question: 'Do you provide actual transaction data for comparable deals?',
      answer: 'No. We provide patent valuation data and portfolio analysis based on patent quality metrics, citation analysis, and industry benchmarks. We do not have access to confidential licensing deal terms or transaction prices.'
    },
    {
      question: 'How current is your patent data?',
      answer: 'Our database updates weekly with new filings, legal status changes, and assignment records from USPTO, WIPO, EPO, and other patent offices. Market data refreshes daily.'
    },
    {
      question: 'Can this replace our IP counsel?',
      answer: 'No. PricePatent provides data and analysis to support your legal team\'s strategy. We recommend using our reports in conjunction with expert legal advice, especially for high-value negotiations.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead
        title="Patent Portfolio Valuation for Cross-Licensing | PricePatent"
        description="Compare patent portfolios instantly for cross-licensing negotiations. Get negotiation-ready valuations in minutes with AI-powered analysis."
        keywords="cross-licensing, patent portfolio valuation, cross-licensing negotiations, patent portfolio comparison, licensing negotiations, IP portfolio analysis"
        ogTitle="Patent Portfolio Valuation for Cross-Licensing | PricePatent"
        ogDescription="Instantly compare your portfolio value against competitors and enter negotiations with data-backed confidence."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Cross-Licensing Portfolio Valuation",
          "description": "AI-powered patent portfolio valuation for cross-licensing negotiations",
          "provider": {
            "@type": "Organization",
            "name": "PricePatent"
          },
          "serviceType": "Patent Portfolio Valuation",
          "category": "Cross-Licensing Solutions"
        }}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-20 pb-24">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3">
            <svg width="800" height="800" viewBox="0 0 800 800" fill="none">
              <circle cx="400" cy="400" r="400" fill="url(#heroGradient)" fillOpacity="0.1" />
              <defs>
                <radialGradient id="heroGradient" cx="0" cy="0" r="1">
                  <stop stopColor="#2563EB" />
                  <stop offset="1" stopColor="#2563EB" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-blue-600 text-sm font-medium mb-6">
              <Layers className="h-4 w-4 mr-2" />
              Cross-Licensing Solution
            </div>
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl mb-6 leading-tight">
              <span className="block pb-1">Patent Portfolio Valuation for</span>
              <span className="block mt-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent pb-1">
                Cross-Licensing Negotiations
              </span>
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600 mb-8">
              Instantly compare your portfolio value against competitors and enter negotiations with data-backed confidence. Just provide patent numbers - we handle the rest.
            </p>
            
            {/* Portfolio Comparison Image */}
            <PortfolioComparisonImage />

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Schedule Demo
                <Calendar className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                See Sample Analysis
                <ExternalLink className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Statement Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Cross-licensing negotiations fail when you can't prove your portfolio's worth
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {painPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{point.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{point.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* How PricePatent Solves This */}
      <div className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Get negotiation-ready portfolio valuations in minutes, not weeks
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Use Case: How It Works */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              From patent numbers to negotiation in 3 steps
            </h2>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 hidden md:block"></div>
            
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex items-start md:items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-full flex items-center justify-center z-10 flex-shrink-0">
                      <div className="text-2xl font-bold text-white">{step.number}</div>
                    </div>
                    <div className="ml-6 md:ml-8 flex-grow">
                      <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-2xl font-semibold text-gray-900">{step.title}</h3>
                          <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                            {step.time}
                          </span>
                        </div>
                        <p className="text-gray-600 text-lg leading-relaxed mb-4">{step.description}</p>
                        {step.details && (
                          <div className="mt-6 space-y-2">
                            {step.number === 2 && (
                              <p className="text-sm font-semibold text-gray-700 mb-2">Our AI evaluates:</p>
                            )}
                            {step.number === 3 && (
                              <p className="text-sm font-semibold text-gray-700 mb-2">Download comprehensive reports showing:</p>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {step.details.map((detail, detailIndex) => (
                                <div key={detailIndex} className="flex items-center bg-blue-50 rounded-lg p-3">
                                  <Check className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                                  <span className="text-gray-700 text-sm">{detail}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Section */}
      <div className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Trusted by IP teams at leading technology companies
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 text-center border border-gray-100">
              <div className="text-3xl font-bold text-blue-600 mb-2">377M+</div>
              <div className="text-gray-600">patents analyzed</div>
            </div>
            <div className="bg-white rounded-lg p-6 text-center border border-gray-100">
              <div className="text-3xl font-bold text-indigo-600 mb-2">220K+</div>
              <div className="text-gray-600">companies valuated</div>
            </div>
            <div className="bg-white rounded-lg p-6 text-center border border-gray-100">
              <div className="text-3xl font-bold text-purple-600 mb-2">30+</div>
              <div className="text-gray-600">valuation factors per patent</div>
            </div>
          </div>
        </div>
      </div>

      {/* API Integration Section */}
      <div className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 border border-gray-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Cpu className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Integrate portfolio valuation into your existing IP management workflow
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto mb-6">
                Enterprise customers can access PricePatent's valuation engine via API for:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {[
                'Valuation updates as patent and market data updates',
                'Bulk processing - Analyze thousands of patents instantly',
                'Due diligence workflows - Integrate into M&A or investment decision processes',
                'Custom dashboards - Display patent valuations in your existing IP management tools',
                'Monitor competitor portfolio values'
              ].map((item, index) => (
                <div key={index} className="flex items-start bg-blue-50 rounded-lg p-4">
                  <Check className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                Contact Sales for API Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Industry-Specific Analysis */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Patent valuation factors vary by technology sector
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{industry.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{industry.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>


      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="h-8 w-8 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl mb-4">
              Enter your next cross-licensing negotiation with confidence
            </h2>
            <p className="mt-4 text-xl text-blue-100 mb-8">
              See how your portfolio compares in minutes. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-blue-600 bg-white hover:bg-blue-50 transition-colors duration-300 shadow-lg hover:shadow-xl"
              >
                Schedule a Demo
                <Calendar className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/demo"
                className="inline-flex items-center justify-center px-8 py-3 border border-white/30 text-base font-medium rounded-lg text-white bg-blue-700 hover:bg-blue-800 transition-colors duration-300"
              >
                Try Free Analysis
                <Zap className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center justify-center px-8 py-3 border border-white/30 text-base font-medium rounded-lg text-white hover:bg-white/10 transition-colors duration-300"
              >
                View Sample Cross-Licensing Report
                <Download className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossLicensingSolution;

